import { z } from 'zod';

// Per plan §9 tooling decision #1: every system boundary parsed by a Zod schema.
// import.meta.env parsing happens once at boot — any failure here is a fatal config error.

const optionalString = <T extends z.ZodTypeAny>(schema: T) =>
  z.preprocess((value) => (value === '' ? undefined : value), schema.optional());

const booleanString = z
  .enum(['true', 'false'])
  .default('false')
  .transform((value) => value === 'true');

const envSchema = z.object({
  VITE_SUPABASE_URL: z.string().url(),
  VITE_SUPABASE_ANON_KEY: z.string().min(20),
  VITE_APP_ENV: z.enum(['development', 'staging', 'production']).default('development'),
  VITE_SENTRY_DSN: optionalString(z.string().url()),
  VITE_APP_BASE_URL: optionalString(z.string().url()),
  VITE_MARKETING_ONLY_MODE: booleanString,
  // VAPID public key (P-256 raw, url-safe-base64). When unset, web push
  // registration is silently disabled and the UI hides the opt-in.
  VITE_VAPID_PUBLIC_KEY: optionalString(z.string().min(20)),
});

export type AppEnv = z.infer<typeof envSchema>;

function parseEnv(): AppEnv {
  const parsed = envSchema.safeParse(import.meta.env);
  if (!parsed.success) {
    const formatted = parsed.error.flatten().fieldErrors;
    // eslint-disable-next-line no-console
    console.error('[env] Invalid environment configuration:', formatted);
    throw new Error(
      `Environment validation failed: ${JSON.stringify(formatted, null, 2)}\n` +
        `Check that all required VITE_* vars are set. See .env.example.`,
    );
  }
  return parsed.data;
}

export const env = parseEnv();
